import { type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import { type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '@/components/common/Card';
interface GenericModuleCardProps {
    // Header section
    icon?: LucideIcon | PhosphorIcon;
    iconBackground?: string;
    title: string;
    titleClassName?: string;
    headerAction?: ReactNode;

    // Content section
    description?: string;
    descriptionClassName?: string;
    contentItems?: {
        icon?: LucideIcon;
        iconClassName?: string;
        iconColor?: string;
        text: string;
        className?: string;
    }[];

    // Footer section
    footer?: ReactNode;

    // Card behavior
    onClick?: () => void;
    className?: string;
    isDragging?: boolean;
    isDisabled?: boolean;

    // Additional content
    children?: ReactNode;
}

const GenericModuleCard = ({
    icon: Icon,
    iconBackground = 'bg-gray-50 text-gray-600',
    title,
    titleClassName = 'text-base font-semibold text-neutral-900',
    headerAction,
    description,
    descriptionClassName = 'text-base text-neutral-500 font-medium line-clamp-3',
    contentItems = [],
    footer,
    className = '',
    isDragging = false,
    isDisabled = false,
    children,
}: GenericModuleCardProps) => {
    return (
        <Card
            className={`flex h-full flex-col gap-5 overflow-hidden bg-neutral-50 p-5 ${isDragging || isDisabled ? 'opacity-50' : ''} ${className}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center pr-2">
                    {Icon && (
                        <div className={`flex-shrink-0 rounded-lg p-2 ${iconBackground}`}>
                            <Icon className="size-6" strokeWidth={1.5} weight="regular" />
                        </div>
                    )}
                    <div className={`min-w-0 flex-1 ${Icon ? 'ml-3' : ''}`}>
                        <div className={titleClassName}>{title}</div>
                    </div>
                </div>
                {headerAction && <div className="flex-shrink-0 self-center">{headerAction}</div>}
            </div>

            {description && <p className={descriptionClassName}>{description}</p>}

            {children && <div className="mt-3">{children}</div>}

            <div className="mt-auto flex flex-col gap-5">
                {contentItems.length > 0 && (
                    <div className="space-y-2">
                        {contentItems.map((item, index) => (
                            <div
                                key={index}
                                className={`flex items-center text-base text-neutral-500 ${item.className || ''}`}
                            >
                                {item.icon && (
                                    <item.icon
                                        className={clsx('size-4 flex-shrink-0 text-neutral-500', item.iconClassName)}
                                        color={item.iconColor || undefined}
                                        strokeWidth={2}
                                    />
                                )}
                                <span className={`font-medium ${item.icon ? 'ml-2' : ''}`}>{item.text}</span>
                            </div>
                        ))}
                    </div>
                )}

                {footer && footer}
            </div>
        </Card>
    );
};

export default GenericModuleCard;
